import React from 'react';
import IntlMessages from 'lngProvider/IntlMessages';
import FormContainer from 'containers/FormContainer';
import { TextField, Button, Typography, Grid, Divider, Paper } from '@mui/material';
import * as Yup from 'yup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import GoogleLogo from 'components/svgs/GoogleLogo';
import FacebookLogo from 'components/svgs/FacebookLogo';
import MicrosoftLogo from 'components/svgs/MicrosoftLogo';
import AppleLogo from 'components/svgs/AppleLogo';
import MMMLogo from 'components/svgs/MMMLogo';
import { EmailValidation } from 'services/EnumConstants';
import { styled } from '@mui/material/styles';
import { colors, spacing } from 'styles/Common';
import { getPaperStyleObject } from 'styles/Paper';

const StyledPaper = styled(Paper)((
    {
        theme
    }
    ) => {
    let paperStyle = getPaperStyleObject(theme);
    return {
        ...paperStyle.root,
        [theme.breakpoints.up('lg')]: {
                padding: spacing[8],
                marginTop: spacing[3],
                marginBottom: spacing[3],
                maxWidth: '600px'
            },
            '& .MuiInputBase-input': {
                height: 30,
                paddingRight: 14,
                paddingTop: 3,
                paddingBottom: 3
            },
            '& .subtitle-text': {
                marginBottom: '24px',
                color: colors.fillWhite
            },
            '& .social-button': {
                backgroundColor: colors.fillWhite,
                color: colors.secondary,
                height: 36,
                '&:hover': {
                    backgroundColor: colors.fillWhite
                }
            },
            '& .forgot-password': {
                textAlign: 'left',
                paddingLeft: spacing[2],
                marginTop: spacing[1],
                marginLeft: spacing[1],
                [theme.breakpoints.up('sm')]: {
                    paddingLeft: '0px',
                    textAlign: 'right'
                }
            },
            '& .sign-in-button': {
                marginLeft: spacing[4],
                marginBottom: spacing[5],
                width: 'calc(100% - 24px)',
                fallbacks: [
                    '-moz-calc(100% - 24px)',
                    '-webkit-calc(100% - 24px)',
                    '-o-calc(100% - 24px)'
                ],
                [theme.breakpoints.up('sm')]: {
                    marginLeft: spacing[1],
                    marginRight: spacing[2],
                    width: '100%'
                }
            },
            '& .error-social-text': {
                textAlign: 'left'
            },
            '& .reg-container': {
                marginLeft: spacing[4]
            },
            '& .activation-email-container': {
                width: `calc(100% + ${spacing[4]}px)`,
                margin: -spacing[1]
            }
        };
    });

const LoginWeb = (props) => {
    const emailRegex = EmailValidation.Production;

    const validationSchema = Yup.object().shape({
        email: Yup
            .string()
            .trim()
            .required(<IntlMessages id="logIn.invalidEmailAddress" />)
            .email(<IntlMessages id="logIn.invalidEmailAddress" />)
            .matches(emailRegex, () => <IntlMessages id="logIn.invalidEmailAddress" />),
        password: Yup
            .string()
            .required(<IntlMessages id="logIn.invalidPassword" />)
            .min(8, true)
    });

    const { data, loginConfig, onSubmit, onSendActivationEmail, onPropsChange, onHandleGoogle, onHandleFacebook, onHandleMicrosoft, onHandleApple, onSsoClick, onEmailChanged } = props;
    const registrationPageUrl = loginConfig.registrationPageUrl;

    return (
        <StyledPaper>
            <Grid container alignItems="center" justifyContent="center" direction="column">
                <Typography variant="h4"><IntlMessages id={loginConfig.loginTitle} /></Typography>
                {loginConfig.loginSubTitle ?
                    <Typography variant="subtitle2"><IntlMessages id={loginConfig.loginSubTitle} /></Typography>
                    : ''}
                <Grid className="subtitle-text"></Grid>
            </Grid>
            <FormContainer
                initialValues={data}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
            >
                {props => {
                    const {
                        values,
                        errors,
                        touched,
                        isSubmitting,
                        handleChange,
                        handleBlur
                    } = props;

                    onPropsChange(props);

                    const handleEmailChanged = (e) => {

                        // pass through to parent
                        handleChange(e);

                        // invoke event handler
                        if (onEmailChanged) {
                            onEmailChanged(e);
                        }
                    }

                    return (
                        <Grid container spacing={3}>
                            {loginConfig.showGoogle === true ?
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={onHandleGoogle}
                                        fullWidth
                                        className="social-button"
                                        startIcon={<GoogleLogo />}>
                                        <IntlMessages id="logIn.continueWithGoogle" />
                                    </Button>
                                </Grid>
                                : ''}
                            {loginConfig.showFacebook === true ?
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={onHandleFacebook}
                                        fullWidth
                                        className="social-button"
                                        startIcon={<FacebookLogo />}>
                                        <IntlMessages id="logIn.continueWithFacebook" />
                                    </Button>
                                </Grid>
                                : ''}
                            {loginConfig.showMicrosoft === true ?
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={() => onHandleMicrosoft(values)}
                                        fullWidth
                                        className="social-button"
                                        startIcon={<MicrosoftLogo />}>
                                        <IntlMessages id="logIn.continueWithMicrosoft" />
                                    </Button>
                                </Grid>
                                : ''}
                            {loginConfig.showApple === true ?
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={onHandleApple}
                                        fullWidth
                                        className="social-button"
                                        startIcon={<AppleLogo />}>
                                        <IntlMessages id="logIn.continueWithApple" />
                                    </Button>
                                </Grid>
                                : ''}
                            {loginConfig.showMMMSso ?
                                <Grid item xs={12}>
                                    <Button
                                        variant="contained"
                                        onClick={onSsoClick}
                                        fullWidth
                                        className="social-button"
                                        startIcon={<MMMLogo />}>
                                        <IntlMessages id="logIn.continueWith3M" />
                                    </Button>
                                </Grid>
                                : ''}

                            {loginConfig.showApple === true || loginConfig.showGoogle === true || loginConfig.showFacebook === true || loginConfig.showMicrosoft === true || loginConfig.showMMMSso === true ?
                                <Grid item xs={12}>
                                    <Divider />
                                </Grid>
                                : ''}
                            {values.socialLogInErrorId && 
                                  <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="error" className="error-social-text">
                                            <IntlMessages id={values.socialLogInErrorId} />
                                        </Typography>
                                  </Grid>}
                            <Grid item xs={12}>
                                <TextField
                                    label={<IntlMessages id="logIn.email" />}
                                    value={values.email}
                                    onChange={handleEmailChanged}
                                    error={errors.email && touched.email}
                                    onBlur={handleBlur}
                                    helperText={((errors.email && touched.email) && errors.email)}
                                    name="email"
                                    inputProps={{
                                        maxLength: 256
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    fullWidth
                                    required />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={<IntlMessages id="logIn.password" />}
                                    value={values.password}
                                    onChange={handleChange}
                                    error={errors.password && touched.password}
                                    onBlur={handleBlur}
                                    helperText={((errors.password && touched.password) && errors.password)}
                                    type="password"
                                    name="password"
                                    inputProps={{
                                        minLength: 8,
                                        maxLength: 256
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    fullWidth
                                    required />
                            </Grid>
                            {values.logInErrorId &&
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2">
                                        <IntlMessages id={values.logInErrorId} />
                                    </Typography>
                                </Grid>}
                            {values.resendActivationEmailVisible === true
                                ? <Grid item className="activation-email-container">
                                    {values.resendActivationEmailSuccessful &&
                                        <Grid item xs={12}>
                                            <CheckCircleIcon className="text-success" /> <IntlMessages id="logIn.resendActivationEmailSuccess" />
                                        </Grid>}
                                    {values.resendActivationEmailFailed &&
                                        <Grid item xs={12}>
                                            <ErrorIcon className="text-danger" /> <IntlMessages id="logIn.resendActivationEmailFailure" />
                                        </Grid>}
                                    {!values.resendActivationEmailSuccessful &&
                                        <Grid item xs={12}>
                                            <InfoIcon className="text-info" />
                                            <IntlMessages id="logIn.needsActivation" />
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                color="primary"
                                                disabled={values.resendActivationEmailInProgress}
                                                startIcon={<EmailIcon />}
                                                onClick={onSendActivationEmail}>
                                                <IntlMessages id="logIn.resendActivationEmail" />
                                            </Button>
                                        </Grid>}
                                </Grid>
                                : <Grid container spacing={4}>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" className="forgot-password">
                                            <a href={`/app/forgotPassword${window.location.search}`}>
                                                <IntlMessages id="logIn.forgotPassword" />
                                            </a>
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            color="primary"
                                            type="submit"
                                            className="sign-in-button"
                                            variant="contained"
                                            disabled={isSubmitting ||
                                                !values.password ||
                                                values.password === '' ||
                                                !!errors.password ||
                                                !values.email ||
                                                values.email === '' ||
                                                !!errors.email}><IntlMessages id={loginConfig.loginButtonText} /></Button>
                                    </Grid>
                                    {registrationPageUrl &&
                                        <Grid item xs={12} className="reg-container">
                                            <Typography variant="subtitle2">
                                                {registrationPageUrl && registrationPageUrl.includes('vas') ?
                                                    <span><IntlMessages id="logIn.freeTrial" /> <a href={registrationPageUrl}><IntlMessages id="logIn.signUp" /></a></span>
                                                    :
                                                    <a href={registrationPageUrl}><IntlMessages id="logIn.createAccount" /></a>
                                                }
                                            </Typography>
                                        </Grid>}
                                </Grid>}
                        </Grid>
                    );

                }}
            </FormContainer>
        </StyledPaper>
    );
}

export default LoginWeb;